// Composer prefill handoff (Tier A / A12 / F11). The mandate composer / Research Home parses a
// document into a MandatePrefill, stashes it in sessionStorage, and navigates to a wizard; the
// wizard reads+clears it on mount and hydrates its form. Only the structured prefill is stored —
// never the raw CIM text. A plain-text "seed" is also supported for chip / quick-launch flows.
// Edit & Re-run also stashes RerunMeta so Target/Strategic can show the banner + mode dialog.
import { useEffect, useRef, useState } from 'react';
import { mandateService, type MandatePrefill, type MandateIntent } from '@/services/api';
import { paths } from '@/routes/paths';
import type { AiAutofillResult } from '@/types';

const KEY = 'quralyst:composerPrefill';
const SEED_KEY = 'quralyst:composerSeed';
const RERUN_KEY = 'quralyst:rerunMeta';

export type RerunMeta = {
  sourceProcessId: string;
  version?: number;
  title?: string;
  /** Composer fetches filters and shows the loading overlay. */
  pendingHydrate?: boolean;
  /** Already-loaded filters so hydrate can merge if reuse-filters is thin. */
  filtersSeed?: Record<string, unknown>;
};

export function composerPathForSource(sourceType: string | undefined, resultId: string): string {
  const kind = (sourceType || '').toLowerCase();
  if (resultId.startsWith('fv_') || kind.includes('financial')) return paths.financialVerticals;
  if (kind.includes('strategic')) return paths.strategic;
  return paths.targetList;
}

/** Stamp Edit & Re-run so the list builder hydrates behind the loading overlay. */
export function beginEditAndRerun(meta: Omit<RerunMeta, 'pendingHydrate'>): void {
  storeRerunMeta({ ...meta, pendingHydrate: true });
}

/** Keep versioning meta after filters have been applied; drop the one-shot hydrate payload. */
export function markRerunHydrated(): void {
  const current = peekRerunMeta();
  if (!current) return;
  storeRerunMeta({
    sourceProcessId: current.sourceProcessId,
    version: current.version,
    title: current.title,
  });
}

export function storeComposerPrefill(prefill: MandatePrefill): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(prefill));
    sessionStorage.removeItem(SEED_KEY);
  } catch {
    /* sessionStorage unavailable (private mode / quota) — prefill just won't carry over */
  }
}

/** Stash a plain-text prompt so the destination wizard can seed its description field. */
export function storeComposerSeed(seed: string): void {
  try {
    sessionStorage.removeItem(KEY);
    if (seed.trim()) sessionStorage.setItem(SEED_KEY, seed.trim());
    else sessionStorage.removeItem(SEED_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
}

export function storeRerunMeta(meta: RerunMeta): void {
  try {
    sessionStorage.setItem(RERUN_KEY, JSON.stringify(meta));
  } catch {
    /* ignore */
  }
}

/** Read rerun meta without clearing — used while the composer is open. */
export function peekRerunMeta(): RerunMeta | null {
  try {
    const raw = sessionStorage.getItem(RERUN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RerunMeta;
    if (!parsed?.sourceProcessId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearRerunMeta(): void {
  try {
    sessionStorage.removeItem(RERUN_KEY);
  } catch {
    /* ignore */
  }
}

export function clearComposerHandoff(): void {
  try {
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(SEED_KEY);
    sessionStorage.removeItem(RERUN_KEY);
  } catch {
    /* ignore */
  }
}

function seedAsPrefill(seed: string): MandatePrefill {
  return {
    business_queries: [seed],
    industry: '',
    sub_industry: '',
    geography: { countries: [], states: [], cities: [] },
    revenue_min: null,
    revenue_max: null,
    ebitda_min: null,
    ebitda_max: null,
    employees_min: null,
    employees_max: null,
    description: seed,
    ideal_buyer_types: [],
  };
}

/** Read and REMOVE the stored prefill (one-shot). Falls back to a plain-text seed. */
export function takeComposerPrefill(): MandatePrefill | null {
  let raw: string | null = null;
  let seed: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    seed = sessionStorage.getItem(SEED_KEY);
    if (raw) sessionStorage.removeItem(KEY);
    if (seed) sessionStorage.removeItem(SEED_KEY);
  } catch {
    return null;
  }
  if (raw) {
    try {
      return JSON.parse(raw) as MandatePrefill;
    } catch {
      /* fall through to seed */
    }
  }
  if (seed && seed.trim()) return seedAsPrefill(seed.trim());
  return null;
}

type SetValue = (name: string, value: unknown, opts?: { shouldDirty?: boolean }) => void;
type GetValues = () => Record<string, unknown>;

/** Apply a mandate prefill onto a Target/Strategic/FV wizard form. Only touches fields the form
 * actually has (guarded via getValues keys). Returns the count of fields set. */
export function applyMandatePrefill(
  prefill: MandatePrefill,
  getValues: GetValues,
  setValue: SetValue,
): number {
  const known = new Set(Object.keys(getValues() ?? {}));
  let applied = 0;
  const set = (name: string, value: unknown) => {
    setValue(name, value, { shouldDirty: true });
    applied += 1;
  };

  const bq = prefill.business_queries?.length
    ? prefill.business_queries.join('\n\n')
    : prefill.description;
  if (bq && known.has('businessQuery')) set('businessQuery.0.value', bq);
  // Financial Verticals uses targetDescription instead of businessQuery.
  if (bq && known.has('targetDescription')) set('targetDescription', bq);

  if (prefill.industry && known.has('industry')) set('industry', prefill.industry);
  if (prefill.sub_industry && known.has('subIndustry')) set('subIndustry', prefill.sub_industry);

  if (known.has('size')) {
    if (prefill.revenue_min != null) set('size.minRevenue', String(prefill.revenue_min));
    if (prefill.revenue_max != null) set('size.maxRevenue', String(prefill.revenue_max));
    if (prefill.employees_min != null) set('size.minEmployees', String(prefill.employees_min));
    if (prefill.employees_max != null) set('size.maxEmployees', String(prefill.employees_max));
  }

  // FV flat size fields
  if (prefill.revenue_min != null && known.has('revenueMin'))
    set('revenueMin', String(prefill.revenue_min));
  if (prefill.revenue_max != null && known.has('revenueMax'))
    set('revenueMax', String(prefill.revenue_max));
  if (prefill.ebitda_min != null && known.has('ebitdaMin'))
    set('ebitdaMin', String(prefill.ebitda_min));
  if (prefill.ebitda_max != null && known.has('ebitdaMax'))
    set('ebitdaMax', String(prefill.ebitda_max));

  const geo = prefill.geography;
  if (geo && known.has('geography')) {
    const base = geo.countries?.length ? geo.countries : [''];
    const groups = base
      .map((country, i) => ({
        country: country || undefined,
        state: geo.states?.[i] ?? geo.states?.[0] ?? undefined,
        city: geo.cities?.[i] ?? geo.cities?.[0] ?? undefined,
      }))
      .filter((g) => g.country || g.state || g.city);
    if (groups.length) set('geography', groups);
  }
  // FV uses flat country/state
  if (geo?.countries?.[0] && known.has('country')) set('country', geo.countries[0]);
  if (geo?.states?.[0] && known.has('state')) set('state', geo.states[0]);

  return applied;
}

/**
 * Apply an AiAutofillResult (from /extract-form-criteria) onto a Target/Strategic composer form,
 * for the "Auto-fill from description" action. The extractor already strips size/location out of the
 * business text, so:
 *   - the description is set to that business-only query,
 *   - Industry / Sub-industry are set,
 *   - Size and Geography are filled ONLY when still empty (never overwrites what the user typed).
 * Returns the number of fields applied (0 = nothing extracted).
 */
export function applyAutofillResult(
  result: AiAutofillResult,
  getValues: GetValues,
  setValue: SetValue,
): number {
  const values = getValues() ?? {};
  const known = new Set(Object.keys(values));
  let applied = 0;
  const set = (name: string, value: unknown) => {
    setValue(name, value, { shouldDirty: true });
    applied += 1;
  };

  // Business-only description (size/location already removed by the extractor).
  if (result.businessQuery) {
    if (known.has('businessQuery')) set('businessQuery.0.value', result.businessQuery);
    if (known.has('targetDescription')) set('targetDescription', result.businessQuery);
  }
  if (result.industry && known.has('industry')) set('industry', result.industry);
  if (result.subIndustry && known.has('subIndustry')) set('subIndustry', result.subIndustry);

  // Size — fill only the empty fields so we never clobber values the user set by hand.
  const size = (values.size ?? {}) as Record<string, unknown>;
  const sizeEmpty = (k: string) => String(size[k] ?? '').trim() === '';
  if (known.has('size')) {
    if (result.minEmployees != null && sizeEmpty('minEmployees'))
      set('size.minEmployees', String(result.minEmployees));
    if (result.maxEmployees != null && sizeEmpty('maxEmployees'))
      set('size.maxEmployees', String(result.maxEmployees));
    if (result.minRevenue != null && sizeEmpty('minRevenue'))
      set('size.minRevenue', String(result.minRevenue));
    if (result.maxRevenue != null && sizeEmpty('maxRevenue'))
      set('size.maxRevenue', String(result.maxRevenue));
  }

  // Geography — only when the whole geography block is still empty (don't disturb existing rows).
  if ((result.country || result.state) && known.has('geography')) {
    const geo = (values.geography ?? []) as Array<Record<string, unknown>>;
    const hasAnyGeo = geo.some((g) => g && (g.continent || g.country || g.state || g.city));
    if (!hasAnyGeo) {
      set('geography', [
        {
          continent: '',
          country: result.country || '',
          region: '',
          state: result.state || '',
          city: '',
        },
      ]);
    }
  }

  return applied;
}

/**
 * Profile a single example-company URL and fold its criteria into a composer form.
 *
 * "Find me buyers/targets like this one": the backend fetches + AI-profiles the URL under the given
 * intent (`example-buyer-profile` on a buyer list, `example-target-profile` on a target list — same
 * UI, different prompt) and returns a MandatePrefill, which `applyMandatePrefill` writes onto the
 * form (overwriting — the example wins). Returns the number of fields applied. Throws if the URL
 * can't be profiled, so the caller can surface the failure and mark the chip.
 */
export async function profileExampleUrl(
  url: string,
  intent: MandateIntent,
  getValues: GetValues,
  setValue: SetValue,
): Promise<number> {
  const prefill = await mandateService.parseMandate({ urls: [url], intent });
  return applyMandatePrefill(prefill, getValues, setValue);
}

/** One-shot hook: on mount, consume any stored prefill and hand it to `onPrefill`. */
export function useComposerPrefill(onPrefill: (prefill: MandatePrefill) => void): void {
  const ran = useRef(false);
  const cb = useRef(onPrefill);
  cb.current = onPrefill;
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const prefill = takeComposerPrefill();
    if (prefill) cb.current(prefill);
  }, []);
}

/** Hold Edit & Re-run meta for the lifetime of the composer page (cleared on successful submit). */
export function useRerunMeta(): RerunMeta | null {
  const [meta] = useState<RerunMeta | null>(() => peekRerunMeta());
  return meta;
}
